// League episodes expose participant scores, so they cannot imply a win or loss.
import { playerLabel } from "@softmax/web-ux/player-label";
import type { LeagueEpisodeRow, LeagueEpisodeSeat } from "../types";
type RoundEpisodeParticipant = {
  readonly position: number;
  readonly kind: "policy" | "human";
  readonly policy_name?: string | null;
  readonly version?: number | null;
  /** `V2EpisodeRequestPolicyParticipant.player_id` — how `rounds.ts` finds the viewer's own seat. */
  readonly player_id?: string | null;
  readonly player_name?: string | null;
  readonly owner_name?: string | null;
};
type RoundEpisodeParticipantScore = {
  readonly position: number;
  readonly score: number;
};
type RoundEpisodeEntry = {
  readonly episode_id?: string | null;
  readonly status: string;
  readonly participants: readonly RoundEpisodeParticipant[];
  readonly participant_scores?: readonly RoundEpisodeParticipantScore[];
  readonly replay_url?: string | null;
  readonly created_at: string;
};
/**
 * "marmot-prime:v12 (David B)" for a policy seat (its owner beside it when a human owns the player), the player's
 * name for a human seat, else a positional fallback — never blank.
 */
function seatLabel(p: RoundEpisodeParticipant): string {
  if (p.kind === "policy" && p.policy_name) {
    const policy =
      p.version !== null && p.version !== undefined
        ? `${p.policy_name}:v${p.version}`
        : p.policy_name;
    return playerLabel({ name: policy, ownerName: p.owner_name ?? null });
  }

  return p.player_name
    ? playerLabel({ name: p.player_name, ownerName: p.owner_name ?? null })
    : `seat ${p.position + 1}`;
}
export function buildLeagueEpisodes({
  entries,
  episodeHrefOf,
  replayHrefOf,
}: {
  readonly entries: readonly RoundEpisodeEntry[];
  readonly episodeHrefOf: (episodeId: string) => string;
  readonly replayHrefOf: (episodeId: string) => string;
}): LeagueEpisodeRow[] {
  const rows: LeagueEpisodeRow[] = [];

  for (const entry of entries) {
    if (entry.status !== "completed" || !entry.episode_id) continue;

    const scoreByPosition = new Map(
      (entry.participant_scores ?? []).map((s) => [s.position, s.score]),
    );
    const seats: LeagueEpisodeSeat[] = [...entry.participants]
      .toSorted((a, b) => a.position - b.position)
      .map((p) => ({
        label: seatLabel(p),
        score: scoreByPosition.get(p.position) ?? null,
      }));
    const scored = seats
      .map((s) => s.score)
      .filter((score): score is number => score !== null);
    rows.push({
      episodeId: entry.episode_id,
      createdAt: entry.created_at,
      seats,
      topScore: scored.length > 0 ? Math.max(...scored) : null,
      // Same rule as `buildEpisodes`: the API's `replay_url` says a replay
      // exists; the door opens the theater's page for it, not the artifact.
      replayHref: entry.replay_url ? replayHrefOf(entry.episode_id) : null,
      episodeHref: episodeHrefOf(entry.episode_id),
    });
  }

  return rows;
}
